import React, { useState, useEffect } from 'react';
import { fetchQuickBooksItems, fetchQuickBooksServiceMappings, saveQuickBooksServiceMappings } from './api';

// Settings › QuickBooks › Service Mapping: pick the QuickBooks item each app service
// pre-selects on invoices, plus the item used for tire disposal lines.
const QuickBooksServiceMapping = () => {
  const [services, setServices] = useState([]);
  const [qbItems, setQbItems] = useState([]);
  const [selected, setSelected] = useState({}); // service key -> qb item id
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const applyMappings = (data) => {
    setServices(data.services);
    setSelected(Object.fromEntries(data.services.map(s => [s.key, s.qb_item_id || ''])));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [mappingData, items] = await Promise.all([
          fetchQuickBooksServiceMappings(),
          fetchQuickBooksItems().catch(() => null),
        ]);
        applyMappings(mappingData);
        if (items) {
          setQbItems(items);
        } else {
          setError('Could not load items from QuickBooks. Check that QuickBooks is connected.');
        }
      } catch (err) {
        console.error('Error loading service mappings:', err);
        setError('Could not load service mappings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const mappings = services.map(s => {
        const itemId = selected[s.key] || '';
        const item = qbItems.find(i => i.id === itemId);
        // Keep the stored name if the item isn't in the current list (e.g. made inactive)
        return { key: s.key, qb_item_id: itemId, qb_item_name: item ? item.name : s.qb_item_name };
      });
      applyMappings(await saveQuickBooksServiceMappings(mappings));
      setMessage('Service mapping saved.');
    } catch (err) {
      console.error('Error saving service mappings:', err);
      setError(err.response?.data?.error || 'Could not save service mapping.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="setting-description">Loading service mapping...</p>;
  }

  return (
    <div>
      <p className="setting-description">
        Choose the QuickBooks item each service should use. It's selected automatically
        when creating an invoice, and can still be changed on the invoice.
      </p>
      {services.map(service => {
        const itemId = selected[service.key] || '';
        const missing = itemId && qbItems.length > 0 && !qbItems.some(i => i.id === itemId);
        return (
          <div className="setting-group" key={service.key}>
            <label className="setting-label">
              {service.label}
              <small style={{ fontWeight: 'normal', marginLeft: '6px', color: '#666' }}>
                ({service.used_by.join(', ')})
              </small>
              <select
                className="setting-input"
                value={itemId}
                onChange={(e) => setSelected(prev => ({ ...prev, [service.key]: e.target.value }))}
              >
                <option value="">Not mapped</option>
                {missing && (
                  <option value={itemId}>{service.qb_item_name || itemId} (not found in QuickBooks)</option>
                )}
                {qbItems.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
          </div>
        );
      })}
      <button className="setting-save-btn" onClick={handleSave} disabled={saving || qbItems.length === 0}>
        {saving ? 'Saving...' : 'Save Mapping'}
      </button>
      {message && <p className="setting-description">{message}</p>}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default QuickBooksServiceMapping;
